/**
 * Application-wide state that is not tied to one image: the vocabulary, readiness (is the
 * database loaded, is a bucket configured), and the one banner that shows a fatal problem.
 *
 * Readiness matters for honesty rather than polish. When `GET /api/settings/status` reports
 * that no bucket is configured, or that `python -m hismaic.db.load` has never run, the work
 * queue must say **that** — including the literal command the API hands back — instead of
 * rendering an empty list that looks like "no work left".
 */

import { api } from '../api/client';
import { ApiProblem, asProblem } from '../api/problem';
import { checkPaletteFont, type FontCheckResult } from '../fontcheck';
import type { SettingsStatus, VocabEntryOut, VocabResponse } from '../api/types';

class AppState {
  vocab = $state<VocabResponse | null>(null);
  status = $state<SettingsStatus | null>(null);
  fatal = $state<ApiProblem | null>(null);
  loading = $state(false);
  fontCheck = $state<FontCheckResult | null>(null);

  byId = $derived.by(() => {
    const map = new Map<number, VocabEntryOut>();
    for (const entry of this.vocab?.classes ?? []) map.set(entry.id, entry);
    return map;
  });

  /** The class used when there is no expected letter to assign to a box. */
  unknownClass = $derived(
    this.vocab?.classes.find((entry) => entry.letter === 'unknown') ?? null,
  );

  letterClasses = $derived((this.vocab?.classes ?? []).filter((entry) => entry.kind === 'letter'));
  specialClasses = $derived((this.vocab?.classes ?? []).filter((entry) => entry.kind !== 'letter'));

  display(letterClass: number): string {
    return this.byId.get(letterClass)?.display ?? `#${letterClass}`;
  }

  get blockers(): string[] {
    return this.status?.blockers ?? [];
  }

  get ready(): boolean {
    return Boolean(this.status?.database_ready) && Boolean(this.vocab?.count);
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      // Status first: it is the endpoint designed to survive an empty database, and its
      // blockers explain why /api/vocab would otherwise 409.
      this.status = await api.settingsStatus();
      try {
        this.vocab = await api.vocab();
      } catch (error) {
        const problem = asProblem(error);
        // `vocab_empty` is a readiness problem, already described in `blockers`. Anything else
        // is not, and gets the fatal banner.
        if (problem.code !== 'vocab_empty') this.fatal = problem;
      }
      if (this.fontCheck === null) this.fontCheck = checkPaletteFont();
    } catch (error) {
      this.fatal = asProblem(error);
    } finally {
      this.loading = false;
    }
  }

  async refreshStatus(): Promise<void> {
    try {
      this.status = await api.settingsStatus();
    } catch (error) {
      this.fatal = asProblem(error);
    }
  }

  dismissFatal(): void {
    this.fatal = null;
  }
}

export const app = new AppState();
